// pages/mypage/MyPageEdit.tsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MyPageLayout from "../../components/mypage/MyPageLayout";
import { getApiUrl, fetchWithAuth } from "../../api";
import { useAuthStore } from "../../store/authStore";

interface MemberMe {
  name: string;
  login_id: string;
  phone: string;
  email: string;
  affiliation_name: string | null;
  affiliation_type: string | null;
  grade: string | null;
}

const AFFILIATION_TYPE_OPTIONS = [
  { value: "", label: "선택 안 함" },
  { value: "elementary", label: "초등학교" },
  { value: "middle", label: "중학교" },
  { value: "high", label: "고등학교" },
  { value: "university", label: "대학교/대학원" },
  { value: "out_of_school", label: "학교 밖" },
  { value: "other", label: "기타" },
  { value: "unknown", label: "미상" },
];

const inputCls =
  "w-full h-11 px-4 border border-[#e5e7eb] rounded-lg text-[14px] text-[#3d2f2a] outline-none focus:border-[#887267] transition-colors bg-white";
const labelCls = "block text-[13px] font-semibold text-[#555] mb-1";

// 비밀번호 형식 검증 (백엔드 PASSWORD_PATTERN과 동일: 8~16자, 영문+숫자+특수문자 모두 포함)
const PW_SPECIAL = /[!@#$%^&*()_+=\-{}\[\]:;"'<>,.?\/\\|~`]/;
const validatePasswordFormat = (pw: string): string => {
  if (pw.length < 8 || pw.length > 16) return "비밀번호는 8~16자로 입력해주세요.";
  if (!/[A-Za-z]/.test(pw)) return "영문을 포함해야 합니다.";
  if (!/\d/.test(pw)) return "숫자를 포함해야 합니다.";
  if (!PW_SPECIAL.test(pw)) return "특수문자를 포함해야 합니다.";
  return "";
};

// FastAPI 422(detail 배열)를 화면에 그대로 렌더하지 않도록 문자열로 변환
const extractDetailMessage = (detail: unknown, fallback = "수정 중 오류가 발생했습니다."): string => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const first = detail[0] as { msg?: string } | undefined;
    if (first?.msg) return first.msg;
  }
  return fallback;
};

export default function MyPageEdit() {
  const navigate = useNavigate();
  const { clearAuth } = useAuthStore();

  const [original, setOriginal] = useState<MemberMe | null>(null);
  const [loading, setLoading] = useState(true);

  // 기본 정보
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [affiliationName, setAffiliationName] = useState("");
  const [affiliationType, setAffiliationType] = useState("");
  const [grade, setGrade] = useState("");

  // 이메일
  const [email, setEmail] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailVerifying, setEmailVerifying] = useState(false);
  const [emailMsg, setEmailMsg] = useState("");

  // 비밀번호
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPwConfirm, setNewPwConfirm] = useState("");
  const [pwMsg, setPwMsg] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  useEffect(() => {
    fetchWithAuth(getApiUrl("/marubit/api/member/me"))
      .then(res => res.ok ? res.json() : null)
      .then((data: MemberMe | null) => {
        if (!data) return;
        setOriginal(data);
        setName(data.name ?? "");
        setPhone(data.phone ?? "");
        setEmail(data.email ?? "");
        setAffiliationName(data.affiliation_name ?? "");
        setAffiliationType(data.affiliation_type ?? "");
        setGrade(data.grade ?? "");
      })
      .catch(e => console.error("회원 정보 로딩 실패:", e))
      .finally(() => setLoading(false));
  }, []);

  const emailChanged = email.trim() !== (original?.email ?? "");

  const handleSendEmailCode = async () => {
    setEmailMsg("");
    setEmailSending(true);
    try {
      const res = await fetchWithAuth(getApiUrl("/marubit/api/member/change-email-verification"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        setEmailSent(true);
        setEmailVerified(false);
        setEmailCode("");
        setEmailMsg("인증번호를 발송했습니다.");
      } else {
        setEmailMsg(data.detail ?? "인증번호 발송에 실패했습니다.");
      }
    } catch {
      setEmailMsg("서버 연결 오류가 발생했습니다.");
    } finally {
      setEmailSending(false);
    }
  };

  const handleVerifyEmailCode = async () => {
    setEmailMsg("");
    setEmailVerifying(true);
    try {
      const res = await fetchWithAuth(getApiUrl("/marubit/api/member/change-email-verification/verify"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), email_verification: emailCode.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        setEmailVerified(true);
        setEmailMsg("이메일 인증이 완료되었습니다.");
      } else {
        setEmailMsg(data.detail ?? "인증번호가 올바르지 않습니다.");
      }
    } catch {
      setEmailMsg("서버 연결 오류가 발생했습니다.");
    } finally {
      setEmailVerifying(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    if (emailChanged && !emailVerified) {
      setErrorMsg("이메일 변경 시 인증을 완료해야 합니다.");
      return;
    }

    const changingPw = currentPw || newPw || newPwConfirm;
    if (changingPw) {
      if (!currentPw) { setErrorMsg("현재 비밀번호를 입력해주세요."); return; }
      if (!newPw) { setErrorMsg("새 비밀번호를 입력해주세요."); return; }
      const pwFormatMsg = validatePasswordFormat(newPw);
      if (pwFormatMsg) { setPwMsg(pwFormatMsg); setErrorMsg(pwFormatMsg); return; }
      if (newPw !== newPwConfirm) { setErrorMsg("새 비밀번호가 일치하지 않습니다."); return; }
    }

    const body: Record<string, string | null> = {
      name: name.trim() || null,
      phone: phone.trim() || null,
      affiliation_name: affiliationName.trim() || null,
      affiliation_type: affiliationType || null,
      grade: grade.trim() || null,
    };

    if (emailChanged && emailVerified) {
      body.email = email.trim();
    }

    if (changingPw) {
      body.current_password = currentPw;
      body.new_password = newPw;
    }

    setSubmitting(true);
    try {
      const res = await fetchWithAuth(getApiUrl("/marubit/api/member/me/update"), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok) {
        if (data.logout_required) {
          clearAuth();
          navigate("/auth/login");
          return;
        }
        setSuccessMsg(data.message ?? "내 정보가 수정되었습니다.");
        setCurrentPw("");
        setNewPw("");
        setNewPwConfirm("");
        setPwMsg("");
      } else {
        setErrorMsg(extractDetailMessage(data.detail));
      }
    } catch {
      setErrorMsg("서버 연결 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MyPageLayout title="내 정보 수정">
      {loading ? (
        <div className="py-24 text-center text-[14px] text-gray-300">불러오는 중...</div>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">

          {/* 기본 정보 */}
          <div className="bg-white rounded-2xl border border-[#f0ebe7] shadow-[0_2px_12px_rgba(136,114,103,0.08)] px-7 py-6">
            <p className="m-0 mb-5 text-[15px] font-bold text-[#3d2f2a]">기본 정보</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>이름</label>
                <input className={inputCls} value={name} onChange={e => setName(e.target.value)} placeholder="이름" />
              </div>
              <div>
                <label className={labelCls}>휴대폰번호</label>
                <input className={inputCls} value={phone} onChange={e => setPhone(e.target.value)} placeholder="01012345678" />
              </div>
              <div>
                <label className={labelCls}>소속명</label>
                <input className={inputCls} value={affiliationName} onChange={e => setAffiliationName(e.target.value)} placeholder="학교명 또는 소속 기관명" />
              </div>
              <div>
                <label className={labelCls}>소속 유형</label>
                <select
                  className={inputCls}
                  value={affiliationType}
                  onChange={e => setAffiliationType(e.target.value)}
                >
                  {AFFILIATION_TYPE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>학년</label>
                <input className={inputCls} value={grade} onChange={e => setGrade(e.target.value)} placeholder="예: 3" />
              </div>
            </div>
          </div>

          {/* 이메일 변경 */}
          <div className="bg-white rounded-2xl border border-[#f0ebe7] shadow-[0_2px_12px_rgba(136,114,103,0.08)] px-7 py-6">
            <p className="m-0 mb-5 text-[15px] font-bold text-[#3d2f2a]">이메일 변경</p>
            <div className="flex flex-col gap-3 max-w-[480px]">
              <div>
                <label className={labelCls}>이메일</label>
                <div className="flex gap-2">
                  <input
                    className={`${inputCls} flex-1`}
                    type="email"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setEmailSent(false); setEmailVerified(false); setEmailMsg(""); }}
                    placeholder="이메일 주소"
                  />
                  {emailChanged && !emailVerified && (
                    <button
                      type="button"
                      onClick={handleSendEmailCode}
                      disabled={emailSending}
                      className="shrink-0 px-4 h-11 bg-[#887267] text-white text-[13px] font-semibold rounded-lg border-none cursor-pointer hover:bg-[#6b5248] transition-colors disabled:opacity-50"
                    >
                      {emailSending ? "발송 중..." : emailSent ? "재발송" : "인증번호 발송"}
                    </button>
                  )}
                  {emailVerified && (
                    <span className="shrink-0 h-11 px-4 flex items-center text-[13px] font-semibold text-green-600 bg-green-50 rounded-lg border border-green-200">
                      인증 완료
                    </span>
                  )}
                </div>
              </div>

              {emailSent && !emailVerified && (
                <div>
                  <label className={labelCls}>인증번호</label>
                  <div className="flex gap-2">
                    <input
                      className={`${inputCls} flex-1`}
                      value={emailCode}
                      onChange={e => setEmailCode(e.target.value)}
                      placeholder="6자리 인증번호"
                      maxLength={6}
                    />
                    <button
                      type="button"
                      onClick={handleVerifyEmailCode}
                      disabled={emailVerifying}
                      className="shrink-0 px-4 h-11 bg-[#887267] text-white text-[13px] font-semibold rounded-lg border-none cursor-pointer hover:bg-[#6b5248] transition-colors disabled:opacity-50"
                    >
                      {emailVerifying ? "확인 중..." : "확인"}
                    </button>
                  </div>
                </div>
              )}

              {emailMsg && (
                <p className={`m-0 text-[13px] ${emailVerified ? "text-green-600" : "text-red-500"}`}>
                  {emailMsg}
                </p>
              )}
            </div>
          </div>

          {/* 비밀번호 변경 */}
          <div className="bg-white rounded-2xl border border-[#f0ebe7] shadow-[0_2px_12px_rgba(136,114,103,0.08)] px-7 py-6">
            <p className="m-0 mb-1 text-[15px] font-bold text-[#3d2f2a]">비밀번호 변경</p>
            <p className="m-0 mb-5 text-[13px] text-gray-400">변경하지 않으려면 비워두세요.</p>
            <div className="flex flex-col gap-4 max-w-[480px]">
              <div>
                <label className={labelCls}>현재 비밀번호</label>
                <input
                  className={inputCls}
                  type="password"
                  value={currentPw}
                  onChange={e => { setCurrentPw(e.target.value); setPwMsg(""); }}
                  placeholder="현재 비밀번호"
                  autoComplete="current-password"
                />
              </div>
              <div>
                <label className={labelCls}>새 비밀번호</label>
                <input
                  className={`${inputCls} ${newPw && validatePasswordFormat(newPw) ? "border-red-400 focus:border-red-400" : ""}`}
                  type="password"
                  value={newPw}
                  onChange={e => { setNewPw(e.target.value); setPwMsg(""); }}
                  placeholder="8~16자, 영문/숫자/특수문자 포함"
                  autoComplete="new-password"
                />
                {newPw && (
                  validatePasswordFormat(newPw)
                    ? <p className="m-0 mt-1 text-[12px] text-red-500">{validatePasswordFormat(newPw)}</p>
                    : <p className="m-0 mt-1 text-[12px] text-green-600">사용 가능한 비밀번호입니다.</p>
                )}
              </div>
              <div>
                <label className={labelCls}>새 비밀번호 확인</label>
                <input
                  className={`${inputCls} ${newPwConfirm && newPw !== newPwConfirm ? "border-red-400 focus:border-red-400" : ""}`}
                  type="password"
                  value={newPwConfirm}
                  onChange={e => setNewPwConfirm(e.target.value)}
                  placeholder="새 비밀번호 재입력"
                  autoComplete="new-password"
                />
                {newPwConfirm && newPw !== newPwConfirm && (
                  <p className="m-0 mt-1 text-[12px] text-red-500">비밀번호가 일치하지 않습니다.</p>
                )}
              </div>
              {pwMsg && <p className="m-0 text-[13px] text-red-500">{pwMsg}</p>}
            </div>
          </div>

          {/* 결과 메시지 */}
          {errorMsg && (
            <div className="px-5 py-3 bg-red-50 border border-red-200 rounded-xl text-[13px] text-red-600">
              {errorMsg}
            </div>
          )}
          {successMsg && (
            <div className="px-5 py-3 bg-green-50 border border-green-200 rounded-xl text-[13px] text-green-700">
              {successMsg}
            </div>
          )}

          {/* 버튼 */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate("/mypage")}
              className="px-6 py-[10px] text-[14px] font-semibold text-gray-500 bg-[#f5f0ec] rounded-[10px] border-none cursor-pointer hover:bg-[#ede8e4] transition-colors"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-[10px] text-[14px] font-semibold text-white bg-[#887267] rounded-[10px] border-none cursor-pointer hover:bg-[#6b5248] transition-colors disabled:opacity-50"
            >
              {submitting ? "저장 중..." : "저장"}
            </button>
          </div>

        </form>
      )}
    </MyPageLayout>
  );
}
